/**
 * SharedEvalsInboxCard — Coach Dashboard (B-2026-0020 Tier 2)
 *
 * Shows the coach a list of evaluations athletes have shared with them that
 * have not yet received a comment from the coach ("awaiting feedback").
 *
 * Data source: GET /api/users/me/evaluations?shared_with_me=true. The
 * listing query populates `comment_count_by_me` per row via a single
 * correlated subquery (no N+1); the card filters client-side to items
 * where comment_count_by_me === 0 ("the coach hasn't replied yet").
 *
 * Three states:
 * - Loading: spinner.
 * - Empty: "No shared evaluations awaiting your feedback."
 * - Populated: list of evals (athlete name, template, score, relative time).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import { ScoreRing } from '@/components/ui/ScoreRing';
import { getMyEvaluations } from '@/services/evaluationApi';
import type { EvaluationSummaryItem } from '@/services/evaluationApi';
import { formatRelativeTime } from './formatRelativeTime';

// ─── Constants ────────────────────────────────────────────────────────────────

const INBOX_LIMIT = 10;

// ─── Inbox row ────────────────────────────────────────────────────────────────

function InboxRow({ evaluation }: { evaluation: EvaluationSummaryItem }) {
  // Deep-link to the comments section of the evaluation page.
  const href = `/evaluations/${evaluation.evaluation_id}#comments`;

  // Prefer user_display_name or created_by_display_name fields if present on the item.
  // These are populated by the backend when the eval was submitted on behalf of a user.
  const athleteName =
    evaluation.user_display_name ??
    evaluation.created_by_display_name ??
    'Athlete';

  const relTime = formatRelativeTime(evaluation.created_at);

  return (
    <Link to={href} style={{ textDecoration: 'none', display: 'block' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderRadius: '10px',
          border: '1px solid var(--color-border-subtle)',
          backgroundColor: 'var(--color-dark-elevated-1)',
          transition: 'border-color 140ms ease, background-color 140ms ease',
          marginBottom: '8px',
          cursor: 'pointer',
          gap: '12px',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'rgba(61, 169, 252, 0.35)';
          el.style.backgroundColor = 'rgba(61, 169, 252, 0.04)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'var(--color-border-subtle)';
          el.style.backgroundColor = 'var(--color-dark-elevated-1)';
        }}
      >
        {/* Left: athlete name + template */}
        <div style={{ minWidth: 0, flex: '1 1 0' }}>
          <p
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '13px',
              color: 'var(--color-text-primary)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {athleteName}
          </p>
          <p
            style={{
              fontSize: '11px',
              color: 'var(--color-text-muted)',
              margin: '2px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {evaluation.template_name ?? '—'} · {relTime}
          </p>
        </div>

        {/* Right: score ring */}
        {typeof evaluation.overall_score === 'number' && (
          <div style={{ flexShrink: 0 }}>
            <ScoreRing score={evaluation.overall_score} size={34} />
          </div>
        )}
      </div>
    </Link>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Card placed on the CoachDashboard showing evaluations athletes have shared
 * with the coach that are awaiting the coach's first comment.
 */
export function SharedEvalsInboxCard() {
  const { data: allShared = [], isLoading } = useQuery({
    queryKey: ['my-evaluations', { shared_with_me: true }],
    queryFn: () => getMyEvaluations({ shared_with_me: true }),
    staleTime: 30_000,
  });

  // Filter to evaluations the coach has not yet commented on. The listing
  // populates `comment_count_by_me` per row server-side; absent → treat as 0.
  const unanswered = useMemo(() => {
    return allShared
      .filter((e: EvaluationSummaryItem) => (e.comment_count_by_me ?? 0) === 0)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      .slice(0, INBOX_LIMIT);
  }, [allShared]);

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InboxArrowDownIcon
            style={{ width: '18px', height: '18px', color: 'var(--color-accent-blue)' }}
          />
          <CardTitle>Shared Evaluations Inbox</CardTitle>
          {!isLoading && unanswered.length > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px',
                height: '20px',
                borderRadius: '999px',
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-text-inverse)',
                fontSize: '11px',
                fontWeight: 700,
                padding: '0 5px',
                marginLeft: '2px',
              }}
            >
              {unanswered.length}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <Spinner size="sm" label="Loading shared evaluations..." />
          </div>
        ) : unanswered.length === 0 ? (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0' }}>
            No shared evaluations awaiting your feedback.
          </p>
        ) : (
          <div>
            {unanswered.map((evaluation) => (
              <InboxRow key={evaluation.evaluation_id} evaluation={evaluation} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
